/**
 * DRIS Corporate Identity Color System
 *
 * This centralized color palette defines all colors used across the DRIS application,
 * following the Corporate Identity guidelines.
 */

export const drisCorporateColors = {
  // Neutral Colors
  veryLightGray: '#F1F1F1',

  // Primary Brand Colors
  blue: '#272BB8',
  darkGreen: '#487F3E',
  lightGreen: '#6DA328',

  // Status/Alert Colors
  limeGreen: '#D6DC3F',
  yellow: '#E7CD39',
  orange: '#DE5614',
  lightOrange: '#ffa000',
  brightRed: '#CF2323',
  darkRed: '#8B0C09',

  // Nutrient Status Colors (5-tier DRIS scale)
  nutrientVeryHigh: '#2B71C1',
  nutrientHigh: '#39B0F1',
  nutrientOptimum: '#92D051',
  nutrientLow: '#FAC100',
  nutrientVeryLow: '#F90202',

  // Accent Colors
  veryDarkPurple: '#20121F',
  darkPurple: '#460B56',
  mediumPurple: '#64245B',
  maroon: '#685f51',

  // Base Colors (keeping for compatibility)
  white: '#FFFFFF',
  black: '#000000',
};

/**
 * The 5-tier nutrient cell pairs (fill `bg`, text `fg`): the one source every leaf, soil, Agent-portal and
 * Staging matrix reads (DRI-108 AC8). WCAG 2.x contrast, AA body text needs 4.5:1:
 * veryHigh 5.0:1, high 7.4:1, optimum 9.7:1, low 10.9:1, veryLow 5.0:1 (black; white was 4.2:1).
 * The fills are the brand tier colours and are unchanged.
 */
export const nutrientTierPairs = {
  veryHigh: { bg: drisCorporateColors.nutrientVeryHigh, fg: drisCorporateColors.white },
  high: { bg: drisCorporateColors.nutrientHigh, fg: drisCorporateColors.veryDarkPurple },
  optimum: { bg: drisCorporateColors.nutrientOptimum, fg: drisCorporateColors.veryDarkPurple },
  low: { bg: drisCorporateColors.nutrientLow, fg: drisCorporateColors.veryDarkPurple },
  veryLow: { bg: drisCorporateColors.nutrientVeryLow, fg: drisCorporateColors.black },
} as const;

export type NutrientTier = keyof typeof nutrientTierPairs;

// Semantic color mapping for application use
export const drisSemanticColors = {
  // Primary actions and brand
  primary: {
    main: drisCorporateColors.darkGreen,
    light: drisCorporateColors.lightGreen,
    dark: drisCorporateColors.darkGreen,
    contrastText: drisCorporateColors.white,
  },

  // Secondary actions
  secondary: {
    main: drisCorporateColors.blue,
    light: drisCorporateColors.blue,
    dark: drisCorporateColors.blue,
    contrastText: drisCorporateColors.white,
  },

  // Status indicators
  success: {
    main: drisCorporateColors.lightGreen,
    light: drisCorporateColors.limeGreen,
    dark: drisCorporateColors.darkGreen,
    contrastText: drisCorporateColors.white,
  },

  warning: {
    main: drisCorporateColors.yellow,
    light: drisCorporateColors.limeGreen,
    dark: drisCorporateColors.orange,
    contrastText: '#000000',
  },

  error: {
    main: drisCorporateColors.brightRed,
    light: drisCorporateColors.orange,
    dark: drisCorporateColors.darkRed,
    contrastText: drisCorporateColors.white,
  },

  info: {
    main: drisCorporateColors.blue,
    light: drisCorporateColors.blue,
    dark: drisCorporateColors.blue,
    contrastText: drisCorporateColors.white,
  },

  // Backgrounds and surfaces
  background: {
    default: drisCorporateColors.veryLightGray,
    paper: drisCorporateColors.white,
  },

  // Text colors
  text: {
    primary: drisCorporateColors.veryDarkPurple,
    secondary: drisCorporateColors.darkPurple,
    disabled: '#9E9E9E',
  },

  // Dividers and borders
  divider: '#E0E0E0',
};

// Analysis type colors (updated to use corporate colors)
export const drisAnalysisColors = {
  sap: drisCorporateColors.lightGreen,      // Green for sap analysis
  tissue: drisCorporateColors.blue,          // Blue for tissue analysis
  soil: drisCorporateColors.maroon,          // Maroon for soil analysis
};

// Chart colors for data visualization
export const drisChartColors = [
  drisCorporateColors.darkGreen,
  drisCorporateColors.blue,
  drisCorporateColors.orange,
  drisCorporateColors.mediumPurple,
  drisCorporateColors.yellow,
  drisCorporateColors.brightRed,
  drisCorporateColors.limeGreen,
  drisCorporateColors.darkPurple,
  drisCorporateColors.maroon,
];

// Performance scale colors (from best to worst)
export const drisPerformanceScale = {
  excellent: drisCorporateColors.darkGreen,
  good: drisCorporateColors.lightGreen,
  fair: drisCorporateColors.limeGreen,
  average: drisCorporateColors.yellow,
  belowAverage: drisCorporateColors.orange,
  poor: drisCorporateColors.brightRed,
  veryPoor: drisCorporateColors.darkRed,
};

/**
 * WCAG 2.1 AA Compliant Accessible Colors
 * These colors meet minimum contrast ratios for accessibility:
 * - Normal text: 4.5:1 contrast ratio
 * - Large text: 3:1 contrast ratio
 * Use these for status indicators and severity colors.
 */
export const drisAccessibleColors = {
  danger: '#B91C1C',      // Red-700: 5.9:1 contrast on white
  warning: '#B45309',     // Amber-700: 4.8:1 contrast on white
  caution: '#92400E',     // Amber-800: 6.4:1 contrast on white
  success: '#15803D',     // Green-700: 4.5:1 contrast on white
  neutral: '#6B7280',     // Gray-500: 4.6:1 contrast on white
  disabled: '#9CA3AF',    // Gray-400: 3.0:1 contrast on white (large text only)
};

/**
 * WCAG 2.1 AA Compliant Workflow Colors
 * These colors are specifically designed for workflow status indicators,
 * meeting minimum contrast ratios for accessibility:
 * - All combinations meet 4.5:1 minimum contrast ratio
 */
export const drisWorkflowColors = {
  /**
   * Workflow status colors.
   * All colors meet WCAG AA contrast requirements (4.5:1 minimum).
   */
  status: {
    NEW: { bg: '#E3F2FD', text: '#1565C0', outline: '#1565C0' },              // Light Blue - 5.3:1 PASS
    AWAITING_PO: { bg: '#FFF3E0', text: '#E65100', outline: '#E65100' },      // Light Orange - 4.6:1 PASS
    AWAITING_IMPORT: { bg: '#FFF8E1', text: '#B36200', outline: '#B36200' },  // Light Amber - 4.8:1 PASS
    AWAITING_REVIEW: { bg: '#EDE7F6', text: '#4527A0', outline: '#4527A0' },  // Light Indigo - 8.55:1 PASS
    AWAITING_SEND: { bg: '#E8F5E9', text: '#2E7D32', outline: '#2E7D32' },    // Light Green - 4.5:1 PASS
    AWAITING_INVOICE: { bg: '#E0F7FA', text: '#00838F', outline: '#00838F' }, // Light Cyan - 4.5:1 PASS
    COMPLETED: { bg: '#E8F5E9', text: '#1B5E20', outline: '#1B5E20' },        // Light Green, darker text - 7.2:1 PASS
  },
  priority: {
    Urgent: { bg: '#CF2323', text: '#FFFFFF' },           // Bright Red - 5.3:1 PASS
    High: { bg: '#B45309', text: '#FFFFFF' },             // Accessible Orange - 4.8:1 PASS
    Normal: { bg: '#6B7280', text: '#FFFFFF' },           // Accessible Grey - 4.6:1 PASS
    Low: { bg: '#272BB8', text: '#FFFFFF' },              // Blue - 8.2:1 PASS
  },
};

// CSS Variables for direct usage
export const colorCSSVariables = `
  /* Corporate Colors */
  --dris-very-light-gray: ${drisCorporateColors.veryLightGray};
  --dris-blue: ${drisCorporateColors.blue};
  --dris-dark-green: ${drisCorporateColors.darkGreen};
  --dris-light-green: ${drisCorporateColors.lightGreen};
  --dris-lime-green: ${drisCorporateColors.limeGreen};
  --dris-yellow: ${drisCorporateColors.yellow};
  --dris-orange: ${drisCorporateColors.orange};
  --dris-light-orange: ${drisCorporateColors.lightOrange};
  --dris-bright-red: ${drisCorporateColors.brightRed};
  --dris-dark-red: ${drisCorporateColors.darkRed};
  --dris-very-dark-purple: ${drisCorporateColors.veryDarkPurple};
  --dris-dark-purple: ${drisCorporateColors.darkPurple};
  --dris-medium-purple: ${drisCorporateColors.mediumPurple};
  --dris-maroon: ${drisCorporateColors.maroon};

  /* Semantic Colors */
  --dris-primary: ${drisSemanticColors.primary.main};
  --dris-secondary: ${drisSemanticColors.secondary.main};
  --dris-success: ${drisSemanticColors.success.main};
  --dris-warning: ${drisSemanticColors.warning.main};
  --dris-error: ${drisSemanticColors.error.main};
  --dris-info: ${drisSemanticColors.info.main};
`;